import { ReactNode, useMemo } from "react";
import {
	Alert,
	Badge,
	Card,
	Col,
	Form,
	ListGroup,
	Row,
	Spinner,
	Table,
} from "react-bootstrap";
import getCountryISO2 from "country-iso-3-to-2";
import ReactCountryFlag from "react-country-flag";
import { Rider } from "../../types/model";
import {
	formatDateValue,
	hasMeaningfulPhaseData,
	RiderHistoryItem,
	RiderHistoryPhase,
	SeasonOption,
} from "./athleteProfileUtils";
import "./AthleteProfileTemplate.css";

type TeamHistoryEntry = {
	team?:
		| string
		| {
				_id?: string;
				name?: string;
				shortName?: string;
		  };
	season?:
		| string
		| {
				_id?: string;
				name?: string;
		  };
};

interface AthleteProfileTemplateProps {
	rider?: Rider | null;
	riderNameFallback?: string;
	history: RiderHistoryItem[];
	seasonHistory: RiderHistoryItem[];
	seasonOptions: SeasonOption[];
	selectedSeasonKey: string;
	onSeasonChange: (seasonKey: string) => void;
	isLoading?: boolean;
	error?: string;
	actions?: ReactNode;
}

const formatPhaseValue = (phase?: RiderHistoryPhase) => {
	if (!hasMeaningfulPhaseData(phase)) {
		return <span className="text-muted">-</span>;
	}

	const rank = (phase?.rank ?? 0) > 0 ? `#${phase?.rank}` : "-";
	const points =
		phase?.points !== undefined && phase?.points !== null
			? `${phase.points} pts`
			: "-";

	return (
		<div className="d-flex flex-column">
			<span className="fw-semibold">{rank}</span>
			<span className="small text-muted">{points}</span>
		</div>
	);
};

const getValueLabel = (value?: number | null) => {
	if (value === undefined || value === null || Number.isNaN(value)) {
		return "-";
	}
	return value.toString();
};

const AthleteProfileTemplate = ({
	rider,
	riderNameFallback,
	history,
	seasonHistory,
	seasonOptions,
	selectedSeasonKey,
	onSeasonChange,
	isLoading = false,
	error = "",
	actions,
}: AthleteProfileTemplateProps) => {
	const riderName = `${rider?.first_name || ""} ${rider?.last_name || ""}`.trim();
	const displayName = riderName || riderNameFallback || "Athlete";
	const riderCountry = (rider?.country || "").toUpperCase().trim();
	const countryIso = getCountryISO2(riderCountry);

	const showQualifyingColumn = useMemo(
		() =>
			seasonHistory.some((historyItem) =>
				hasMeaningfulPhaseData(historyItem.qualifying_data),
			),
		[seasonHistory],
	);

	const showSemisColumn = useMemo(
		() =>
			seasonHistory.some((historyItem) =>
				hasMeaningfulPhaseData(historyItem.semis_data),
			),
		[seasonHistory],
	);

	const selectedSeasonPoints = useMemo(
		() =>
			seasonHistory.reduce(
				(total, item) => total + (Number(item.total_points ?? 0) || 0),
				0,
			),
		[seasonHistory],
	);

	const bestFinalRank = useMemo(() => {
		const ranks = seasonHistory
			.map((item) => item.finals_data?.rank)
			.filter((rank): rank is number => typeof rank === "number" && rank > 0);
		if (ranks.length === 0) {
			return undefined;
		}
		return Math.min(...ranks);
	}, [seasonHistory]);

	const topThreeFinishes = useMemo(
		() =>
			seasonHistory.filter(
				(item) =>
					typeof item.finals_data?.rank === "number" &&
					item.finals_data.rank > 0 &&
					item.finals_data.rank <= 3,
			).length,
		[seasonHistory],
	);

	const sports = (rider?.sports || []) as Array<string | { name?: string }>;
	const series = (rider?.series || []) as Array<string | { name?: string }>;
	const teamHistory = (rider?.teamHistory || []) as TeamHistoryEntry[];

	const sportNames = sports
		.map((item) => (typeof item === "string" ? item : item?.name || ""))
		.filter((item) => item)
		.join(", ");

	const seriesNames = series
		.map((item) => (typeof item === "string" ? item : item?.name || ""))
		.filter((item) => item)
		.join(", ");

	const teamHistorySummary = teamHistory
		.map((entry) => {
			const teamLabel =
				typeof entry.team === "string"
					? entry.team
					: entry.team?.shortName || entry.team?.name || "";
			const seasonLabel =
				typeof entry.season === "string"
					? entry.season
					: entry.season?.name || "";
			const value = `${seasonLabel}${seasonLabel && teamLabel ? " • " : ""}${teamLabel}`;
			return value.trim();
		})
		.filter((entry) => entry)
		.slice(0, 6);

	const riderInitials = `${rider?.first_name?.[0] || ""}${rider?.last_name?.[0] || ""}`.toUpperCase();

	return (
		<Card className="athlete-profile-template border-0 shadow-sm">
			<Card.Body>
				<div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
					<div>
						<h4 className="mb-1">{displayName}</h4>
						<div className="d-flex flex-wrap gap-2 align-items-center text-muted small">
							{countryIso ? (
								<ReactCountryFlag
									countryCode={countryIso}
									svg
									style={{ width: "1.5rem", height: "1rem" }}
								/>
							) : (
								rider?.country || "-"
							)}
							{rider?.gender && <span>{`Gender: ${rider.gender}`}</span>}
							{rider?.status && <Badge bg="secondary">{rider.status}</Badge>}
						</div>
					</div>
					{actions ? <div className="d-flex gap-2">{actions}</div> : null}
				</div>

				<Row className="g-3 mb-3">
					<Col xs={12} md={4} lg={3}>
						<div className="athlete-profile-template__image-wrap">
							{rider?.imageUrl ? (
								<img
									src={rider.imageUrl}
									alt={displayName}
									className="athlete-profile-template__image"
								/>
							) : (
								<div className="athlete-profile-template__placeholder">
									{riderInitials || "--"}
								</div>
							)}
						</div>
					</Col>
					<Col xs={12} md={8} lg={9}>
						<Row className="g-2">
							<Col xs={6} lg={3}>
								<div className="athlete-profile-template__metric">
									<div className="text-muted small">Events</div>
									<div className="fw-bold fs-5">{seasonHistory.length}</div>
								</div>
							</Col>
							<Col xs={6} lg={3}>
								<div className="athlete-profile-template__metric">
									<div className="text-muted small">Points</div>
									<div className="fw-bold fs-5">{selectedSeasonPoints}</div>
								</div>
							</Col>
							<Col xs={6} lg={3}>
								<div className="athlete-profile-template__metric">
									<div className="text-muted small">Best Final</div>
									<div className="fw-bold fs-5">
										{bestFinalRank ? `#${bestFinalRank}` : "-"}
									</div>
								</div>
							</Col>
							<Col xs={6} lg={3}>
								<div className="athlete-profile-template__metric">
									<div className="text-muted small">Top 3</div>
									<div className="fw-bold fs-5">{topThreeFinishes}</div>
								</div>
							</Col>
						</Row>

						<div className="mt-3">
							<div className="small text-muted">Current Team</div>
							<div className="fw-semibold">{rider?.team || "-"}</div>
						</div>
						<div className="mt-2">
							<div className="small text-muted">Date of Birth</div>
							<div className="fw-semibold">{rider?.DOB || "-"}</div>
						</div>
						<div className="mt-2">
							<div className="small text-muted">Sports</div>
							<div className="fw-semibold">{sportNames || "-"}</div>
						</div>
						<div className="mt-2">
							<div className="small text-muted">Series</div>
							<div className="fw-semibold">{seriesNames || "-"}</div>
						</div>
					</Col>
				</Row>

				{teamHistorySummary.length > 0 && (
					<div className="mb-3">
						<div className="small text-muted mb-1">Team History</div>
						<ListGroup horizontal className="flex-wrap gap-2 border-0">
							{teamHistorySummary.map((entry, index) => (
								<ListGroup.Item
									key={`${entry}-${index}`}
									className="rounded-pill border-0 athlete-profile-template__team-history"
								>
									{entry}
								</ListGroup.Item>
							))}
						</ListGroup>
					</div>
				)}

				<div className="d-flex flex-wrap gap-2 justify-content-between align-items-end mb-3">
					<div className="d-flex flex-wrap gap-2">
						<Badge bg="secondary">{history.length} total events</Badge>
						<Badge bg="info">
							{selectedSeasonPoints} season points
						</Badge>
					</div>
					<Form.Group>
						<Form.Label className="fw-semibold mb-1">Season</Form.Label>
						<Form.Select
							size="sm"
							value={selectedSeasonKey}
							onChange={(event) => onSeasonChange(event.target.value)}
							className="w-auto"
							disabled={seasonOptions.length === 0}
						>
							{seasonOptions.map((seasonOption) => (
								<option key={seasonOption.key} value={seasonOption.key}>
									{seasonOption.label}
								</option>
							))}
						</Form.Select>
					</Form.Group>
				</div>

				{isLoading && (
					<div className="d-flex justify-content-center py-4">
						<Spinner animation="border" role="status" />
					</div>
				)}

				{error && !isLoading && (
					<Alert variant="danger" className="mb-0">
						{error}
					</Alert>
				)}

				{!isLoading && !error && seasonHistory.length === 0 && (
					<Alert variant="info" className="mb-0">
						No available rider history for the selected season.
					</Alert>
				)}

				{!isLoading && !error && seasonHistory.length > 0 && (
					<Table responsive striped hover bordered className="align-middle mb-0" size="sm">
						<thead>
							<tr>
								<th>Event</th>
								<th>Date</th>
								<th>Series</th>
								{showQualifyingColumn && <th>Quali</th>}
								{showSemisColumn && <th>Semis</th>}
								<th>Finals</th>
								<th>Total</th>
							</tr>
						</thead>
						<tbody>
							{seasonHistory.map((item) => (
								<tr key={item._id || `${item.event?.name}-${item.event_date}`}>
									<td>
										<div className="fw-semibold">{item.event?.name || "-"}</div>
										<div className="small text-muted">
											{item.event?.location || "-"}
											{item.event?.round ? ` • Round ${item.event.round}` : ""}
										</div>
									</td>
									<td>{formatDateValue(item.event_date || item.event?.date)}</td>
									<td>
										<div>{item.series?.name || "-"}</div>
										<div className="small text-muted">{item.sport?.name || "-"}</div>
									</td>
									{showQualifyingColumn && (
										<td>{formatPhaseValue(item.qualifying_data)}</td>
									)}
									{showSemisColumn && <td>{formatPhaseValue(item.semis_data)}</td>}
									<td>{formatPhaseValue(item.finals_data)}</td>
									<td className="fw-bold">{getValueLabel(item.total_points)}</td>
								</tr>
							))}
						</tbody>
					</Table>
				)}
			</Card.Body>
		</Card>
	);
};

export default AthleteProfileTemplate;
